import { isEqual } from 'lodash';
import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { search } from 'sr-types';
import { UserContext } from '../auth/UserContext';
import { StepsIndexConstant } from '../components/Stepper';
import { useStepper } from '../components/StepperContext';
import type { Entity } from '../constants/customSortFieldConfigs';
import { FormContainer } from '../form/FormContainer';
import { FormProvider } from '../form/FormContext';
import FormPanel from '../form/FormPanel';
import RenderLexicalEditor from '../form/LexicalTemplateEditor';
import { Filter } from '../types/savedFilters';
import { failedToastMessage } from '../utils/commonUtils';
import { getTemplateById, prepareTemplateWithPlaceholders } from './reportUtils';
import SaveAsTemplateFormModal from './SaveAsTemplateFormModal';

export default function StepCustomizeReport({
    facets,
    appliedFilters,
    entity,
    preferenceEntityKey,
    reportContextTerm,
    setReportTitle
}: {
    facets?: search.SkFacetSet[];
    appliedFilters?: Filter[];
    entity: Entity;
    preferenceEntityKey: string;
    reportContextTerm?: string;
    setReportTitle: Dispatch<SetStateAction<string>>;
}) {
    const { getStepData, activeStep, setStepData } = useStepper();
    const currentData = getStepData(activeStep);
    const reportTemplateStep = getStepData(StepsIndexConstant.zero);
    const { preferences, application } = useContext(UserContext);
    const parsedData = typeof currentData?.rich === 'string' ? JSON.parse(currentData?.rich) : currentData?.rich;

    const [saveAsModalOpen, setSaveAsModalOpen] = useState<boolean>(false);

    const selectedTemplate = useMemo(() => {
        return getTemplateById(reportTemplateStep?.selectedId, preferences);
    }, [preferences, reportTemplateStep?.selectedId]);

    const resolvedTemplate = useMemo(() => {
        try {
            return prepareTemplateWithPlaceholders(parsedData || selectedTemplate, reportTemplateStep.reportTitle);
        } catch (err) {
            failedToastMessage('report.load.failed', err);
            return selectedTemplate;
        }
    }, [parsedData, reportTemplateStep?.reportTitle, selectedTemplate]);

    const [values, setValues] = useState<Record<string, any>>({
        rich: resolvedTemplate,
        selectedId: reportTemplateStep?.selectedId ?? null
    });

    useEffect(() => {
        if (!isEqual(currentData, values)) {
            setStepData(activeStep, values);
        }
    }, [activeStep, currentData, setStepData, values]);

    return (
        <FormProvider validationRules={undefined} state={values} setState={setValues} allowMultipleExpansion>
            <FormPanel>
                <FormContainer>
                    <RenderLexicalEditor
                        isJsonContent
                        entity={entity}
                        setSaveAsModalOpen={setSaveAsModalOpen}
                        preferenceEntityKey={preferenceEntityKey}
                        reportContextTerm={reportContextTerm}
                    />
                </FormContainer>
                <SaveAsTemplateFormModal
                    saveAsModalOpen={saveAsModalOpen}
                    setSaveAsModalOpen={setSaveAsModalOpen}
                    saveAsName={reportTemplateStep?.reportTitle}
                    values={values}
                    application={application}
                    entity={entity}
                    preferenceEntityKey={preferenceEntityKey}
                    setReportTitle={setReportTitle}
                />
            </FormPanel>
        </FormProvider>
    );
}
